<template>
  <div
    ref="dropdown"
    tabindex="0"
    :class="[$style['dropdown'], { [$style['dropdown--bordered']]: bordered }, { [$style['dropdown--error']]: hasError }]"
    @keydown.enter.self="show = !show">
    <div
      ref="content"
      :class="[
        $style['dropdown__wrapper'],
        { [$style['dropdown--show']]: show }
      ]">
      <div
        :class="[
          $style['dropdown__selected'],
          bold ? $style['dropdown__selected__bold'] : null
        ]"
        data-test-id="dropdown-selected"
        @click.stop="show = !show">
        <div>
          <slot name="input-wrapper" />
        </div>
        <div :class="$style['dropdown__item']">
          <slot name="selected" />
        </div>
      </div>
      <div
        v-if="show"
        :class="$style['dropdown__items']">
        <div
          v-for="(item, n) in items"
          :key="n"
          tabindex="0"
          :class="[
            $style['dropdown__item'],
            { [$style['dropdown__item--selected']]: item === selected }
          ]"
          data-test-id="dropdown-item"
          @keydown.enter.self="onInput(item)"
          @click.stop="onInput(item)">
          <slot
            name="item"
            :item="item" />
        </div>
      </div>

      <div
        class="h-hide-mobile-up"
        :class="[
          $style['dropdown__background'],
          { [$style['dropdown__background--show']]: show }
        ]"
        @click="show = false" />

      <FormValidation :validation="validation" />
    </div>
  </div>
</template>
<script setup>
import { onClickOutside } from '@vueuse/core'
const emit = defineEmits(['input', 'reset', 'hide', 'show'])

const props = defineProps({
  selected: {
    type: [Number, String, Object, Boolean],
    default: null
  },
  items: {
    type: [Array, Object],
    default: null
  },
  bordered: {
    type: Boolean,
    default: false
  },
  hasError: {
    type: [Boolean],
    default: false
  },
  bold: {
    type: Boolean,
    default: false
  },
  searchDropdown: {
    type: Boolean,
    default: false
  },
  validation: {
    type: [Object],
    default: null
  }
})

const show = ref(false)
const dropdown = ref()
const content = ref()
let yOffeset = 0

watch(() => props.selected, () => {
  show.value = false
  emit('reset')
})

watch(() => show.value, () => {
  if (show.value) {
    emit('show')
    return
  }
  emit('hide')
})

onMounted(() => {
  onClickOutside(content, hide)
  yOffeset = dropdown.value.getBoundingClientRect().y - 275
})

onUnmounted(() => {
  window.removeEventListener('touchmove', handleScroll)
  window.removeEventListener('scroll', handleScroll)
})

const hide = () => {
  show.value = false
  emit('reset')
}
const onInput = item => {
  show.value = false
  emit('input', item)
  emit('reset')
}

const handleScroll = () => {
  window.scrollTo({
    top: yOffeset
  })
}

watch(() => show.value, () => {
  if (!process.client) { return }
  if (show.value && props.searchDropdown && window.innerWidth < 520) {
    setTimeout(() => {
      window.scrollTo({ top: yOffeset, behavior: 'smooth' })
    }, 100)
    window.addEventListener('touchmove', handleScroll)
    window.addEventListener('scroll', handleScroll)
    return
  }
  window.removeEventListener('touchmove', handleScroll)
  window.removeEventListener('scroll', handleScroll)
})

</script>
<style lang="scss" module>
.dropdown {
  border-radius: $border-radius;

  &__background {
    background-color: $backdrop;
    overscroll-behavior: contain;

    width: 100vw;
    height: 100vh;
    position: fixed;
    left: 0;
    top: 0;
    display: none;
    z-index: z-index(dialog);

    &--show {
      display: flex;
    }
  }

  &__wrapper {
    position: relative;
    height: 100%;
  }

  &__item {
    align-items: center;
    border-bottom: 1px solid $grey-light;
    cursor: pointer;
    display: flex;
    height: auto;
    padding: $spacing * 1.375; // match standard input height & padding for consistency
    padding-left: $spacing * 2;
    width: 100%;

    &:focus,
    &:hover {
      background-color: $grey-lighter;
    }

    &--selected {
      font-weight: $font-weight-bold;
    }

    img {
      height: auto;
      width: $spacing * 2.5;
      margin-right: $spacing;
      max-width: 100px;
    }

    &:last-child {
      border: 0;
    }
  }

  &__selected {
    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><g><path fill='none' d='M0 0h24v24H0z'/><path d='M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z'/></g></svg>");
    background-repeat: no-repeat;
    background-position: right $spacing * 1.5 top 50%;
    background-size: $spacing * 3 auto;
    border-radius: $border-radius;

    // box-shadow: $box-shadow-input;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    height: 100%;
    white-space: nowrap;
    width: 100%;

    .dropdown__item {
      &:focus,
      &:hover {
        background-color: transparent;
      }
    }

    &__bold {
      font-weight: bold;
    }
  }

  &__items {
    background-color: $white;
    border-radius: $border-radius;
    box-shadow: $box-shadow;
    max-height: 300px;
    flex-direction: column;
    position: absolute;
    z-index: z-index(dialog) + 1;
    min-width: 300px;
    opacity: 1;
    overflow-y: auto;
    overscroll-behavior: none;
    width: 100%;

    &--show {
      display: flex;
    }

    @media (max-width: #{$breakpoint-mobile}) {
      box-shadow: $box-shadow-large;
      display: flex;
      position: absolute;
      bottom: $spacing * 6;
      max-height: 200px;
      left: 0;
      right: 0;
      width: auto;
    }
  }

  &--bordered {
    border: 1px solid $grey-light;

    &:hover,
    &:focus {
      border-color: $black;
    }
  }

  &--error {
    border-bottom-color: $red;
  }
}
</style>
